import { z } from 'zod';
import { editionSchluessel, funktionen, lizenzLaufzeiten, lizenzSchema } from './contracts.js';
/**
 * Verträge der **Verwaltung** (netable) — `apps/admin-api` und `apps/admin-web`
 * — und der internen Schnittstelle, über die die Verwaltung mit jedem
 * Partner-Stack spricht (ADR-0013).
 *
 * Die Verwaltung kennt Partner, Preise, Aufträge und Mengen. Mengen kommen
 * täglich aus dem Partner-Stack, **je Kunde aufgeschlüsselt** (Entscheidung
 * des Inhabers vom 12.09.2026: Der Partner soll seine Rechnung prüfen können
 * — „Kunde A: 7, Kunde B: 12"). Was ein Kunde intern hat — Anwender, Geräte,
 * Rufnummern, Gespräche — bleibt im Stack.
 */
// ---------------------------------------------------------------------------
// Sitzung
// ---------------------------------------------------------------------------
export const verwaltungSitzungSchema = z.object({
    kontoId: z.string(),
    anzeigename: z.string(),
    email: z.string(),
});
// ---------------------------------------------------------------------------
// Partner
// ---------------------------------------------------------------------------
export const partnerZustaende = ['aktiv', 'gesperrt', 'gekuendigt'];
const hostname = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?)+$/, 'Kein gültiger Hostname');
/** Ein Teil eines Hostnamens: klein, ohne Punkt. */
const subdomainSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/, 'Keine gueltige Subdomain');
/** Wie der letzte Abruf beim Partner-Stack ausging. */
export const abrufStandSchema = z.object({
    am: z.string(),
    status: z.enum(['ok', 'fehler']),
    fehler: z.string().nullable(),
    /** Wie viele Kunden der Abruf gemeldet hat. */
    kunden: z.number().int().nonnegative(),
});
export const verwaltungPartnerSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Abrechnungsbezug — die Kennung im Rechnungssystem (Autotask, weclapp). */
    billingRef: z.string(),
    zustand: z.enum(partnerZustaende),
    /** Aufträge des Partners ohne Freigabe durch netable ausführen. */
    autoApprove: z.boolean(),
    whiteLabel: z.boolean(),
    baseDomain: z.string().nullable(),
    domainVerifiedAt: z.string().nullable(),
    /**
     * Subdomain des Partners unter der Plattform-Domain (ADR-0015): seine
     * Kundenverwaltung unter `<subdomain>.<plattform>`, seine Kunden unter
     * `<kunde>.<subdomain>.<plattform>`. Bei White-Label gilt die eigene Domain.
     */
    subdomain: z.string().nullable(),
    /** Suffix der Kunden-Hosts, wie er aus Subdomain oder White-Label-Domain folgt; `null` ohne beides. */
    kundenDomain: z.string().nullable(),
    /** Server, auf dem der Stack läuft — für Rollout und Kapazität. */
    stackHost: z.string().nullable(),
    /** Interne Adresse des Stacks, an die die Verwaltung ihre Abrufe schickt. */
    stackEndpoint: z.string().nullable(),
    /** Der gekoppelte Server (ADR-0020); `null`, solange keiner gekoppelt ist. */
    server: z.lazy(() => partnerServerSchema).nullable(),
    /** Jüngste Hinterlegung des Hauptschlüssels; `null`, solange keine da ist. */
    hinterlegung: z.lazy(() => partnerHinterlegungSchema).nullable(),
    /** Welche Fassung dieser Stack fahren soll; `null` = die, die er hat. */
    freigegebeneVersion: z.string().nullable(),
    /** Bis wann ein offener Kopplungscode gilt; `null` = keiner offen. */
    kopplungscodeOffenBis: z.string().nullable(),
    /** Jüngster Kopplungsversuch der Verwaltung beim Stack — solange ein Code offen ist. */
    kopplungsversuch: z.lazy(() => kopplungsversuchSchema).nullable(),
    notizen: z.string().nullable(),
    angelegtAm: z.string(),
    letzterAbruf: abrufStandSchema.nullable(),
});
export const verwaltungPartnerListeSchema = z.object({
    partner: z.array(verwaltungPartnerSchema),
});
export const partnerAnlegenSchema = z.object({
    name: z.string().trim().min(2).max(120),
    billingRef: z.string().trim().min(1).max(80),
    autoApprove: z.boolean().default(false),
    whiteLabel: z.boolean().default(false),
    baseDomain: hostname.nullable().default(null),
    subdomain: subdomainSchema.nullable().default(null),
    stackHost: z.string().trim().max(200).nullable().default(null),
    stackEndpoint: z.url().nullable().default(null),
    notizen: z.string().trim().max(2000).nullable().default(null),
});
export const partnerAendernSchema = z
    .object({
    name: z.string().trim().min(2).max(120).optional(),
    billingRef: z.string().trim().min(1).max(80).optional(),
    zustand: z.enum(partnerZustaende).optional(),
    autoApprove: z.boolean().optional(),
    whiteLabel: z.boolean().optional(),
    baseDomain: hostname.nullable().optional(),
    subdomain: subdomainSchema.nullable().optional(),
    stackHost: z.string().trim().max(200).nullable().optional(),
    stackEndpoint: z.url().nullable().optional(),
    notizen: z.string().trim().max(2000).nullable().optional(),
    /** Fassung, die der Stack fahren soll (ADR-0020). */
    freigegebeneVersion: z
        .string()
        .regex(/^\d+\.\d+\.\d+$/)
        .nullable()
        .optional(),
})
    .refine((wert) => Object.values(wert).some((v) => v !== undefined), {
    message: 'Mindestens ein Feld muss angegeben sein',
});
/** Das Stack-Geheimnis — genau einmal sichtbar, beim Erzeugen. */
// ---------------------------------------------------------------------------
// Preise — VK je Edition ab Monat, Abweichungen je Partner (ADR-0015)
// ---------------------------------------------------------------------------
/** Ein VK-Satz der Edition, gueltig ab Monat, bis ein juengerer greift. */
export const editionPreisSchema = z.object({
    gueltigAb: z.string().regex(/^\d{4}-\d{2}$/),
    /** Verkaufspreis je Seed und Monat, netto, in Cent. */
    vkCent: z.number().int().nonnegative(),
    angelegtAm: z.string(),
});
export const editionPreisAnlegenSchema = editionPreisSchema.pick({ gueltigAb: true, vkCent: true });
export const preisabweichungArten = ['preis', 'rabatt'];
/**
 * Abweichung eines Partners vom VK: fester Preis (Cent) oder Rabatt (Prozent),
 * gueltig ab Monat, wahlweise bis Monat. Ein fester Preis hat Vorrang vor
 * einem Rabatt.
 */
export const preisabweichungSchema = z.object({
    id: z.string(),
    edition: editionSchluessel,
    art: z.enum(preisabweichungArten),
    /** Cent bei `preis`, Prozent bei `rabatt`. */
    wert: z.number().int().nonnegative(),
    gueltigAb: z.string().regex(/^\d{4}-\d{2}$/),
    gueltigBis: z
        .string()
        .regex(/^\d{4}-\d{2}$/)
        .nullable(),
    angelegtAm: z.string(),
});
export const preisabweichungAnlegenSchema = preisabweichungSchema
    .pick({ edition: true, art: true, wert: true, gueltigAb: true, gueltigBis: true })
    .refine((w) => w.art !== 'rabatt' || w.wert <= 100, { message: 'Rabatt hoechstens 100 %' });
/** Der wirksame Preis einer Edition fuer einen Partner in einem Monat — und woher er kommt. */
export const wirksamerPreisSchema = z.object({
    edition: editionSchluessel,
    monat: z.string().regex(/^\d{4}-\d{2}$/),
    vkCent: z.number().int().nonnegative().nullable(),
    seedCent: z.number().int().nonnegative().nullable(),
    quelle: z.enum(['vk', 'preis', 'rabatt', 'keiner']),
    abweichung: preisabweichungSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Mengen — was der Stack meldet, was die Verwaltung daraus rechnet
// ---------------------------------------------------------------------------
export const kundenZustaende = ['bereitstellung', 'aktiv', 'deaktiviert', 'geloescht'];
/** Ein Kunde, wie der Stack ihn der Verwaltung meldet — Name und Mengen, sonst nichts. */
export const stackKundeSchema = z.object({
    /** Der Host des Kunden — die stabile Kennung über alle Abrufe. */
    ref: z.string(),
    name: z.string(),
    edition: editionSchluessel,
    zustand: z.enum(kundenZustaende),
    /** Seeds: aktive Anwender mit sipgate-Verknuepfung — `null`, wenn die Kundendatenbank nicht antwortet. */
    seeds: z.number().int().nonnegative().nullable(),
    istDemo: z.boolean(),
    aktivSeit: z.string().nullable(),
    /** Die Lizenz des Kunden (ADR-0015); `null` ohne Kauf. */
    lizenz: lizenzSchema.nullable(),
});
/** `GET /intern/verwaltung/mengen` im Partner-Stack. */
export const stackMengenSchema = z.object({
    stand: z.string(),
    kunden: z.array(stackKundeSchema),
});
/** Ein Tagesstand je Kunde, wie die Verwaltung ihn speichert. */
export const mengenTagKundeSchema = stackKundeSchema.extend({
    tag: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
/** Peak eines Kunden im Monat. */
export const monatsKundeSchema = z.object({
    ref: z.string(),
    name: z.string(),
    edition: editionSchluessel,
    istDemo: z.boolean(),
    /** Höchster Tagesstand im Monat. */
    seedsPeak: z.number().int().nonnegative(),
    /** An wie vielen Tagen der Kunde aktiv gemeldet war. */
    tageAktiv: z.number().int().nonnegative(),
    /** Seit wann der Kunde aktiv ist (aus dem Stack); `null`, wenn unbekannt. */
    aktivSeit: z.string().nullable(),
    /** Der juengste Tag im Monat, an dem sich Seeds oder Edition gegenueber dem Vortag geaendert haben — sonst der erste Tagesstand. */
    letzteAenderung: z.string().nullable(),
    /** Lizenz nach dem juengsten Stand des Monats; `null` ohne Kauf. */
    lizenz: lizenzSchema.nullable(),
    /** Was berechnet wird: das Maximum aus Peak und Mindestmenge der Lizenz. */
    menge: z.number().int().nonnegative(),
});
/** Mengen eines Partners in einem Monat — die Abrechnungsgrundlage (§4). */
export const monatsmengenSchema = z.object({
    monat: z.string().regex(/^\d{4}-\d{2}$/),
    /** An wie vielen Tagen des Monats ein Abruf gelungen ist. */
    abrufTage: z.number().int().nonnegative(),
    /** Peak aktiver, nicht-Demo-Kunden an einem Tag. */
    kundenPeak: z.number().int().nonnegative(),
    /** Peak der Seeds je Edition an einem Tag, Demo-Kunden ausgenommen — Schluessel ist der Editionsschluessel. */
    seedsPeak: z.record(editionSchluessel, z.number().int().nonnegative()),
    jeKunde: z.array(monatsKundeSchema),
});
/** Die Tagesstände eines Monats — die Aufschlüsselung, mit der ein Partner nachrechnet. */
export const tagesstaendeSchema = z.object({
    monat: z.string().regex(/^\d{4}-\d{2}$/),
    tage: z.array(z.object({
        tag: z.string(),
        kunden: z.array(stackKundeSchema),
    })),
});
// ---------------------------------------------------------------------------
// Abrechnung — Mengen × Preise je Partner und Monat, zur Übergabe
// ---------------------------------------------------------------------------
export const abrechnungsartikel = ['seed', 'kunde', 'plattform'];
export const abrechnungspositionSchema = z.object({
    artikel: z.enum(abrechnungsartikel),
    /** Bei `seed` der Editionsschluessel, sonst `null`. */
    edition: editionSchluessel.nullable(),
    /** Bei `seed` die Laufzeit — monatliche Lizenzen tragen den Aufschlag; `null` ohne Lizenz. */
    laufzeit: z.enum(lizenzLaufzeiten).nullable(),
    bezeichnung: z.string(),
    menge: z.number().int().nonnegative(),
    einzelpreisCent: z.number().int().nonnegative(),
    gesamtCent: z.number().int().nonnegative(),
});
export const abrechnungSchema = z.object({
    partnerId: z.string(),
    partnerName: z.string(),
    billingRef: z.string(),
    monat: z.string().regex(/^\d{4}-\d{2}$/),
    waehrung: z.literal('EUR'),
    /** Editionen, die im Monat vorkamen, aber keinen Preis hatten — dann stehen dort nur Mengen. */
    ohnePreis: z.array(editionSchluessel),
    positionen: z.array(abrechnungspositionSchema),
    gesamtCent: z.number().int().nonnegative(),
    mengen: monatsmengenSchema,
});
export const abrechnungenSchema = z.object({
    monat: z.string(),
    abrechnungen: z.array(abrechnungSchema),
});
// ---------------------------------------------------------------------------
// Aufträge — Bereitstellungsaufträge aus dem Partner-Stack
// ---------------------------------------------------------------------------
export const auftragZustaende = [
    'wartet_auf_freigabe',
    'freigegeben',
    'laeuft',
    'fertig',
    'fehlgeschlagen',
    'zurueckgezogen',
];
/** Ein Auftrag, wie der Stack ihn meldet — nur, was die Freigabe braucht (§5.1). */
export const stackAuftragSchema = z.object({
    id: z.string(),
    art: z.string(),
    zustand: z.enum(auftragZustaende),
    host: z.string(),
    anzeigename: z.string(),
    edition: editionSchluessel.nullable(),
    seatCap: z.number().int().nonnegative().nullable(),
    istDemo: z.boolean(),
    beantragtAm: z.string(),
    beantragtVon: z.string().nullable(),
    fehler: z.string().nullable(),
});
export const stackAuftraegeSchema = z.object({ auftraege: z.array(stackAuftragSchema) });
/** Die Sicht der Verwaltung: Auftrag plus Partner. */
export const verwaltungAuftragSchema = stackAuftragSchema.extend({
    partnerId: z.string(),
    partnerName: z.string(),
});
export const verwaltungAuftraegeSchema = z.object({
    auftraege: z.array(verwaltungAuftragSchema),
    /** Partner, deren Stack nicht geantwortet hat. */
    nichtErreichbar: z.array(z.object({ partnerId: z.string(), partnerName: z.string() })),
});
// ---------------------------------------------------------------------------
// Editionen — netable klickt Funktionen zusammen, der Partner verkauft aus
// seinem Katalog (ADR-0014)
// ---------------------------------------------------------------------------
export const verwaltungEditionSchema = z.object({
    key: editionSchluessel,
    name: z.string(),
    funktionen: z.array(z.enum(funktionen)),
    /** Ende der Edition, `YYYY-MM-DD`; ab dann verkauft sie kein Partner mehr. `null` = kein Ende. */
    eolAm: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    /** Vorgabe der Mindestlaufzeit neuer Lizenzen in Monaten (ADR-0015). */
    mindestlaufzeitMonate: z.number().int().positive(),
    /** Jahresrabatt in Prozent: jaehrlich der VK, monatlich VK ÷ (1 − Rabatt) — `monatspreisCent`. */
    monatlichAufschlagProzent: z.number().int().nonnegative(),
    /** VK-Saetze, juengster zuerst. */
    preise: z.array(editionPreisSchema),
    /** Der VK des laufenden Monats; `null`, wenn keiner gilt. */
    vkCent: z.number().int().nonnegative().nullable(),
    angelegtAm: z.string(),
    geaendertAm: z.string(),
    /** Bei wie vielen Partnern sie im Katalog steht. */
    partner: z.number().int().nonnegative(),
    /** Wie viele Kunden sie nach dem juengsten Tagesstand haben, ueber alle Partner. */
    kunden: z.number().int().nonnegative(),
});
export const verwaltungEditionenSchema = z.object({ editionen: z.array(verwaltungEditionSchema) });
export const editionAnlegenSchema = z.object({
    key: editionSchluessel,
    name: z.string().trim().min(2).max(80),
    funktionen: z.array(z.enum(funktionen)),
    /** Beides ist Pflicht: Laufzeit und Jahresrabatt stehen an der Edition, nicht im Code. */
    mindestlaufzeitMonate: z.number().int().positive().max(120),
    monatlichAufschlagProzent: z.number().int().nonnegative().max(99),
    /** Erster VK, gueltig ab dem laufenden Monat; ohne ihn hat die Edition noch keinen Preis. */
    vkCent: z.number().int().nonnegative().optional(),
});
export const editionAendernSchema = z
    .object({
    name: z.string().trim().min(2).max(80).optional(),
    funktionen: z.array(z.enum(funktionen)).optional(),
    eolAm: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    mindestlaufzeitMonate: z.number().int().positive().max(120).optional(),
    monatlichAufschlagProzent: z.number().int().nonnegative().max(99).optional(),
})
    .refine((wert) => Object.values(wert).some((v) => v !== undefined), {
    message: 'Mindestens ein Feld muss angegeben sein',
});
/** Katalog eines Partners: welche Editionen er verkaufen darf. */
export const partnerKatalogSchema = z.object({
    editionen: z.array(editionSchluessel),
    /** Abweichende Mindestlaufzeit dieses Partners je Edition; fehlt der Schluessel, gilt die Vorgabe der Edition. */
    mindestlaufzeit: z.record(editionSchluessel, z.number().int().positive()),
    /** Preisabweichungen dieses Partners, juengste zuerst. */
    abweichungen: z.array(preisabweichungSchema),
    /** Wirksame Preise je Katalog-Edition fuer den laufenden Monat. */
    preise: z.array(wirksamerPreisSchema),
    /** Wann der Katalog zuletzt an den Stack ging; `null` = noch nie. */
    verteiltAm: z.string().nullable(),
    /** Fehler der letzten Verteilung, sonst `null`. */
    verteilungFehler: z.string().nullable(),
});
export const partnerKatalogSetzenSchema = z.object({
    editionen: z.array(editionSchluessel),
    mindestlaufzeit: z.record(editionSchluessel, z.number().int().positive().max(120)).default({}),
});
/**
 * `PUT /intern/verwaltung/editionen` im Partner-Stack — der Katalog, den die
 * Verwaltung verteilt: nur die Editionen dieses Partners, mit seinen Preisen.
 * Der Stack ersetzt damit seinen Stand; Editionen, die fehlen, bleiben fuer
 * Bestandskunden bekannt, sind aber nicht mehr im Katalog.
 */
export const stackEditionenSchema = z.object({
    stand: z.string(),
    editionen: z.array(z.object({
        key: editionSchluessel,
        name: z.string(),
        funktionen: z.array(z.enum(funktionen)),
        eolAm: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .nullable(),
        seedCent: z.number().int().nonnegative().nullable(),
        mindestlaufzeitMonate: z.number().int().positive(),
        monatlichAufschlagProzent: z.number().int().nonnegative(),
    })),
    /** Domain-Schema dieses Partners: Kunden-Hosts enden auf `.<kundenSuffix>`; `null` = keine Vorgabe. */
    domain: z.object({ kundenSuffix: z.string().nullable() }),
});
// ---------------------------------------------------------------------------
// Uebersichten der Verwaltung — Partnerliste und Partnerdetail (ADR-0015 Nachtrag)
// ---------------------------------------------------------------------------
/** Eine Zeile der Partnerliste: was der Partner gerade hat und was der laufende Monat kostet. */
export const partnerUebersichtSchema = z.object({
    id: z.string(),
    name: z.string(),
    billingRef: z.string(),
    zustand: z.enum(partnerZustaende),
    kundenDomain: z.string().nullable(),
    stackAngebunden: z.boolean(),
    letzterAbruf: abrufStandSchema.nullable(),
    /** Aktive, nicht-Demo-Kunden nach dem juengsten Tagesstand. */
    kunden: z.number().int().nonnegative(),
    /** Seeds nach dem juengsten Tagesstand, alle Editionen. */
    seeds: z.number().int().nonnegative(),
    /** Gesamtpreis des laufenden Monats nach heutigem Stand. */
    umsatzCent: z.number().int().nonnegative(),
    /** Hat der Partner Preisabweichungen. */
    abweichungen: z.number().int().nonnegative(),
});
export const partnerUebersichtListeSchema = z.object({
    monat: z.string(),
    partner: z.array(partnerUebersichtSchema),
});
/** Kopfzahlen eines Partners: Seeds je Edition heute, Peak je Edition im Monat, Umsatz zweier Monate. */
export const partnerDetailUebersichtSchema = z.object({
    laufenderMonat: z.string(),
    letzterMonat: z.string(),
    seedsJeEdition: z.record(editionSchluessel, z.number().int().nonnegative()),
    peakJeEdition: z.record(editionSchluessel, z.number().int().nonnegative()),
    umsatzLaufendCent: z.number().int().nonnegative(),
    umsatzLetzterCent: z.number().int().nonnegative(),
    /** Nur die Editionen mit abweichendem Preis im laufenden Monat. */
    abweichendePreise: z.array(wirksamerPreisSchema),
});
// ---------------------------------------------------------------------------
// Verankerung der Audit-Wurzeln (ADR-0019)
// ---------------------------------------------------------------------------
/** Ein abgeschlossener Monat eines Audit-Kerns: Root-Hash als Hex. */
export const ankerSchema = z.object({
    period: z.string().regex(/^\d{4}-\d{2}$/),
    rootHash: z.string().regex(/^[0-9a-f]{64}$/),
    eventCount: z.number().int().nonnegative(),
    closedAt: z.string(),
});
/** Was ein Stack der Verwaltung zur Abholung anbietet: Registry und jeder aktive Kunde. */
export const stackAnkerSchema = z.object({
    stand: z.string(),
    registry: z.array(ankerSchema),
    kunden: z.array(z.object({ ref: z.string(), anker: z.array(ankerSchema) })),
});
/**
 * Was die Verwaltung dem Stack schickt: ihre eigenen Wurzeln — und die
 * Bestätigung, welche Wurzeln des Stacks sie abgeholt und festgehalten hat.
 */
export const verwaltungAnkerSchema = z.object({
    stand: z.string(),
    anker: z.array(ankerSchema),
    bestaetigt: z.object({
        registry: z.array(z.string().regex(/^\d{4}-\d{2}$/)),
        kunden: z.array(z.object({ ref: z.string(), perioden: z.array(z.string().regex(/^\d{4}-\d{2}$/)) })),
    }),
});
// ---------------------------------------------------------------------------
// Server-Kopplung (ADR-0020)
// ---------------------------------------------------------------------------
export const fingerabdruckSchema = z.string().regex(/^[0-9a-f]{64}$/);
export const fassungSchema = z.string().regex(/^\d+\.\d+\.\d+$/);
// ---------------------------------------------------------------------------
// Server-Kopplung (ADR-0020, Richtungsumkehr vom 15.09.2026): Die Verwaltung
// ruft den Stack — der Stack ruft nie nach Hause.
// ---------------------------------------------------------------------------
/**
 * Was die Verwaltung an `POST /intern/koppeln` schickt. Die Schranke ist der
 * Code, den der Partner im Stack hinterlegt hat; alles andere ist öffentlich.
 */
export const internKoppelnSchema = z.object({
    code: z.string().trim().min(8).max(20),
    /** Ed25519 der Verwaltung, SPKI-DER, base64 — der Stack pinnt ihn. */
    verwaltungsschluessel: z.string().min(40).max(200),
    verwaltungsFingerabdruck: fingerabdruckSchema,
    /** RSA, SPKI-DER, base64 — dafür verschlüsselt der Stack seine Hinterlegung. */
    wiederherstellungsschluessel: z.string().min(100),
    /** Feste Adressen von netable — nur von dort darf `/intern/*` kommen. Leer: keine Prüfung. */
    adressen: z.array(z.string()),
    partnerId: z.string(),
    partnerName: z.string(),
    freigegebeneVersion: fassungSchema.nullable(),
});
export const internKoppelnAntwortSchema = z.object({
    /** Ed25519 des Servers, SPKI-DER, base64. */
    oeffentlicherSchluessel: z.string().min(40).max(200),
    fingerabdruck: fingerabdruckSchema,
    /** Hülle der Hinterlegung als JSON-Text — die Verwaltung öffnet sie nie. */
    hinterlegung: z.string().min(1).max(16_000),
    schluesselversionen: z.array(z.number().int().positive()).min(1),
    version: fassungSchema,
    build: z.string().max(40).nullable(),
});
/** Standabfrage der Verwaltung — bringt die freigegebene Fassung und die Adressen mit. */
export const internStandSchema = z.object({
    freigegebeneVersion: fassungSchema.nullable(),
    adressen: z.array(z.string()),
});
export const internStandAntwortSchema = z.object({
    fingerabdruck: fingerabdruckSchema,
    version: fassungSchema,
    build: z.string().max(40).nullable(),
    zustand: z.enum(['ok', 'fehler']),
    /** Kurz und ohne Personenbezug — etwa der Grund eines Fehlers. */
    hinweis: z.string().max(500).nullable(),
});
export const serverKopplungscodeSchema = z.object({
    code: z.string(),
    gueltigBis: z.string(),
});
/** Der jüngste Versuch der Verwaltung, einen offenen Code beim Stack einzulösen. */
export const kopplungsversuchSchema = z.object({
    zuletzt: z.string().nullable(),
    /** `null`: noch nicht versucht oder gelungen. Sonst der Grund, knapp. */
    fehler: z.string().nullable(),
});
/** Der gekoppelte Server aus Sicht der Verwaltung. */
export const partnerServerSchema = z.object({
    fingerabdruck: fingerabdruckSchema,
    gekoppeltAm: z.string(),
    /** Adresse, unter der die Verwaltung gekoppelt hat. */
    gekoppeltVon: z.string(),
    version: z.string(),
    build: z.string().nullable(),
    zustand: z.enum(['gekoppelt', 'ok', 'fehler', 'abgemeldet']),
    letzteMeldung: z.string().nullable(),
    abgemeldetAm: z.string().nullable(),
});
export const partnerHinterlegungSchema = z.object({
    erhaltenAm: z.string(),
    schluesselversionen: z.array(z.number().int()),
    wiederherstellungsschluessel: fingerabdruckSchema,
});
// ---------------------------------------------------------------------------
// Einrichtungsassistent der Kundenverwaltung (ADR-0020)
// ---------------------------------------------------------------------------
export const einrichtungZustandSchema = z.object({
    /** Gibt es schon ein Partnerkonto? Dann ist der Assistent vorbei — für immer. */
    eingerichtet: z.boolean(),
    gekoppelt: z.boolean(),
    /** Nur nach der Einrichtung — vorher verrät der Stack nichts. */
    partnerName: z.string().nullable(),
});
/** Schritt 1: Der Partner hinterlegt den Code von netable; die Verwaltung holt ihn ab. */
export const einrichtungCodeSchema = z.object({
    code: z.string().trim().min(8).max(20),
});
export const einrichtungCodeAntwortSchema = z.object({
    /** Einmalig, bis der Code verfällt: Nur damit lässt sich das erste Konto anlegen. */
    einrichtungstoken: z.string().min(20),
    codeBis: z.string(),
});
/** Schritt 2: Warten, bis die Verwaltung gekoppelt hat — nur mit dem Token. */
export const einrichtungWartenSchema = z.object({
    einrichtungstoken: z.string().min(20).max(200),
});
export const einrichtungWartenAntwortSchema = z.object({
    gekoppelt: z.boolean(),
    partnerName: z.string().nullable(),
    fingerabdruck: fingerabdruckSchema.nullable(),
});
export const einrichtungKontoSchema = z.object({
    einrichtungstoken: z.string().min(20).max(200),
    email: z.string().trim().toLowerCase().email().max(254),
    anzeigename: z.string().trim().min(1).max(120),
    passwort: z.string().min(12).max(200),
});
/** Anbindung an netable aus Sicht eines angemeldeten Partner-Admins. */
export const kopplungsstandSchema = z.object({
    gekoppelt: z.boolean(),
    partnerId: z.string().nullable(),
    partnerName: z.string().nullable(),
    fingerabdruck: z.string().nullable(),
    verwaltungsFingerabdruck: z.string().nullable(),
    gekoppeltAm: z.string().nullable(),
    gewuenschteVersion: z.string().nullable(),
    laufendeVersion: z.string(),
    letzteMeldung: z.string().nullable(),
    adressen: z.array(z.string()),
    /** Ein hinterlegter Code wartet noch auf die Verwaltung — bis dann. */
    codeOffenBis: z.string().nullable(),
});
//# sourceMappingURL=verwaltung.js.map